
import { useSession } from "@/hooks/useSession";
import { supabase } from "@/integrations/supabase/client";
import { useCallback } from "react";

export function useProgressInitializer(resourceId: string) {
  const { session } = useSession();

  const initializeProgressRecords = useCallback(async (files: any[]) => {
    if (!session?.user?.id || !files.length) return;
    
    try {
      console.log('Initializing progress tracking for files:', files.map(f => f.id).join(', '));
      
      const { data: existingProgress, error: progressError } = await supabase
        .from('training_progress')
        .select('file_id')
        .eq('resource_id', resourceId)
        .eq('user_id', session.user.id);
        
      if (progressError) {
        console.error('Error fetching existing progress:', progressError);
        return;
      }
      
      const existingFileIds = new Set(existingProgress?.map(p => p.file_id) || []);
      const filesToInitialize = files.filter(file => !existingFileIds.has(file.id));
      
      if (filesToInitialize.length === 0) {
        console.log('All files already have progress records');
        return;
      }
      
      console.log(`Initializing progress records for ${filesToInitialize.length} files`);
      
      // Create progress records for all files at once for performance
      const progressRecords = filesToInitialize.map(file => ({
        resource_id: resourceId,
        file_id: file.id,
        user_id: session.user.id,
        completed: false,
        updated_at: new Date().toISOString()
      }));
      
      const { error: insertError } = await supabase
        .from('training_progress')
        .insert(progressRecords);
        
      if (insertError) {
        console.error('Error initializing progress records:', insertError);
      } else {
        console.log(`Successfully initialized ${progressRecords.length} progress records`);
      }
    } catch (error) {
      console.error('Error in initializeProgressRecords:', error);
    }
  }, [resourceId, session?.user?.id]);

  return { initializeProgressRecords };
}
